<template>
  <div class="h-full flex flex-col">
    <!-- Header & Filters -->
    <div class="px-5 py-3">
      <div class="flex items-center justify-between gap-4">
        <h2 class="text-sm uppercase font-medium tracking-wide text-blue-300">
          Source Monitoring
          <span class="text-slate-400 font-normal">({{ activeTab === 'inputs' ? filteredInputs.length : filteredOutputs.length }})</span>

          <span v-if="loading || loadingStatuses || loadingLiveAssignments" class="ml-1 relative">
            <i class="bi bly-loading text-red-500 animate-spin absolute"></i>
          </span>
        </h2>
      </div>

      <div class="mt-3 flex items-center justify-end gap-3">
        <!-- Tab Toggle -->
        <div class="flex items-center rounded border border-white/15 overflow-hidden">
          <button
            class="px-3 py-1 text-xs font-bold tracking-tight cursor-pointer transition-colors"
            :class="activeTab === 'inputs'
              ? 'bg-blue-700 text-white! shadow-sm'
              : 'text-white hover:bg-slate-700'"
            @click="activeTab = 'inputs'"
          >
            Inputs
          </button>
          <button
            class="px-3 py-1 text-xs font-bold tracking-tight cursor-pointer transition-colors"
            :class="activeTab === 'outputs'
              ? 'bg-blue-700 text-white! shadow-sm'
              : 'text-white hover:bg-slate-700'"
            @click="activeTab = 'outputs'"
          >
            Outputs
          </button>
        </div>

        <div class="hidden md:flex items-center gap-3">
          <!-- Search -->
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="activeTab === 'inputs' ? 'Search inputs...' : 'Search outputs...'"
            class="h-7 w-48 text-xs bg-[#13191d] border border-white/15 rounded px-2 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500"
          />

          <!-- Type Filter -->
          <div class="relative">
            <select
              v-model="selectedType"
              class="h-7 w-44 text-xs bg-[#13191d] border border-white/15 rounded pl-2 pr-6 text-slate-200 focus:outline-none focus:border-blue-500 appearance-none cursor-pointer"
            >
              <option value="" class="bg-[#13191d] text-slate-200">All Types</option>
              <option
                v-for="t in currentAvailableTypes"
                :key="t"
                :value="t"
                class="bg-slate-800 text-slate-200"
              >
                {{ t }}
              </option>
            </select>
            <i class="bi bi-chevron-down absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none"></i>
          </div>

          <!-- Sort -->
          <button
            class="h-7 inline-flex items-center gap-2 rounded border border-white/15 bg-[#13191d] px-2 text-xs font-bold tracking-tight text-slate-200 transition-colors hover:border-white/25 hover:bg-slate-800"
            :title="sortButtonLabel"
            @click="toggleSortBy"
          >
            <svg v-if="!sortNameDesc" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48" id="List-A-To-Z--Streamline-Plump" height="48" width="48" class="h-4 w-4">
              <path fill="currentColor" fill-rule="evenodd" d="M12.2504 6.03773 10.3493 11.741h3.8022l-1.9011 -5.70327Zm7.7229 7.35737L16.5497 3.12412C15.9329 1.27359 14.2011 0.0253906 12.2504 0.0253906c-1.9506 0 -3.68238 1.2481994 -4.29923 3.0987294L4.52752 13.3951c-0.01305 0.0363 -0.02529 0.073 -0.03668 0.11l-1.93438 5.8032c-0.43662 1.3098 0.27128 2.7256 1.58114 3.1622 1.30986 0.4367 2.72566 -0.2712 3.16228 -1.5811l1.38279 -4.1484h7.13553l1.3828 4.1484c0.4366 1.3099 1.8524 2.0178 3.1623 1.5811 1.3098 -0.4366 2.0177 -1.8524 1.5811 -3.1622L20.01 13.5051c-0.0114 -0.037 -0.0236 -0.0737 -0.0367 -0.11Zm9.3658 -3.83351c-0.9743 -0.07414 -1.7188 -1.0857 -1.7558 -2.66428 -0.0067 -0.28582 -0.0106 -0.60165 -0.0106 -0.95004 0 -0.3484 0.0039 -0.66423 0.0106 -0.95004 0.037 -1.57858 0.7815 -2.59015 1.7558 -2.66429 1.2415 -0.09449 3.4318 -0.18841 7.2336 -0.18841s5.9921 0.09392 7.2336 0.18841c0.9743 0.07414 1.7188 1.0857 1.7558 2.66429 0.0067 0.28581 0.0106 0.60164 0.0106 0.95004 0 0.34839 -0.0039 0.66422 -0.0106 0.95003 -0.037 1.57859 -0.7815 2.59014 -1.7558 2.66429 -1.2415 0.09449 -3.4318 0.18841 -7.2336 0.18841s-5.9921 -0.09392 -7.2336 -0.18841Zm0 24.10551c-0.9743 -0.0742 -1.7188 -1.0857 -1.7558 -2.6643 -0.0067 -0.2858 -0.0106 -0.6017 -0.0106 -0.9501 0 -0.3484 0.0039 -0.6642 0.0106 -0.95 0.037 -1.5786 0.7815 -2.5901 1.7558 -2.6643 1.2415 -0.0945 3.4318 -0.1884 7.2336 -0.1884s5.9921 0.0939 7.2336 0.1884c0.9743 0.0742 1.7188 1.0857 1.7558 2.6643 0.0067 0.2858 0.0106 0.6016 0.0106 0.95 0 0.3484 -0.0039 0.6643 -0.0106 0.9501 -0.037 1.5786 -0.7815 2.5901 -1.7558 2.6643 -1.2415 0.0945 -3.4318 0.1884 -7.2336 0.1884s-5.9921 -0.0939 -7.2336 -0.1884Zm-1.7558 9.3357c0.037 1.5786 0.7815 2.5901 1.7558 2.6643 1.2415 0.0945 3.4318 0.1884 7.2336 0.1884s5.9921 -0.0939 7.2336 -0.1884c0.9743 -0.0742 1.7188 -1.0857 1.7558 -2.6643 0.0067 -0.2858 0.0106 -0.6017 0.0106 -0.9501 0 -0.3484 -0.0039 -0.6642 -0.0106 -0.95 -0.037 -1.5786 -0.7815 -2.5901 -1.7558 -2.6643 -1.2415 -0.0945 -3.4318 -0.1884 -7.2336 -0.1884s-5.9921 0.0939 -7.2336 0.1884c-0.9743 0.0742 -1.7188 1.0857 -1.7558 2.6643 -0.0067 0.2858 -0.0106 0.6016 -0.0106 0.95 0 0.3484 0.0039 0.6643 0.0106 0.9501Zm1.7558 -21.3357c-0.9743 -0.0742 -1.7188 -1.0857 -1.7558 -2.6643 -0.0067 -0.2858 -0.0106 -0.6017 -0.0106 -0.9501 0 -0.3484 0.0039 -0.6642 0.0106 -0.95 0.037 -1.5786 0.7815 -2.5901 1.7558 -2.6643 1.2415 -0.0945 3.4318 -0.1884 7.2336 -0.1884s5.9921 0.0939 7.2336 0.1884c0.9743 0.0742 1.7188 1.0857 1.7558 2.6643 0.0067 0.2858 0.0106 0.6016 0.0106 0.95 0 0.3484 -0.0039 0.6643 -0.0106 0.9501 -0.037 1.5786 -0.7815 2.5901 -1.7558 2.6643 -1.2415 0.0944 -3.4318 0.1884 -7.2336 0.1884s-5.9921 -0.094 -7.2336 -0.1884Zm-11.231 3.7343c0.9346 0 1.7913 0.5214 2.2206 1.3516 0.4293 0.8303 0.3597 1.8307 -0.1806 2.5934l-9.6534 13.6284h8.3456c1.3807 0 2.5 1.1193 2.5 2.5s-1.1193 2.5 -2.5 2.5H5.6602c-0.93469 0 -1.79131 -0.5214 -2.22065 -1.3517 -0.42934 -0.8302 -0.35968 -1.8306 0.18059 -2.5933l9.65346 -13.6284H6.39243c-1.38072 0 -2.5 -1.1193 -2.5 -2.5s1.11928 -2.5 2.5 -2.5H18.1081Z" clip-rule="evenodd" stroke-width="1"></path>
            </svg>
            <svg v-if="sortNameDesc" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48" id="List-Z-To-A--Streamline-Plump" height="48" width="48" class="w-4 h-4">
            <path fill="currentColor" fill-rule="evenodd" d="M18.1081 0.0249023c0.9346 0 1.7913 0.5214037 2.2206 1.3516577 0.4293 0.83025 0.3597 1.83065 -0.1806 2.59339L10.4947 17.5983h8.3456c1.3807 0 2.5 1.1193 2.5 2.5 0 1.3808 -1.1193 2.5 -2.5 2.5H5.6602c-0.93469 0 -1.79131 -0.5214 -2.22065 -1.3516 -0.42934 -0.8303 -0.35968 -1.8307 0.18059 -2.5934L13.2736 5.0249H6.39243c-1.38072 0 -2.5 -1.11929 -2.5 -2.5s1.11928 -2.4999977 2.5 -2.4999977H18.1081ZM10.3493 37.1165l1.9011 -5.7033 1.9011 5.7033h-3.8022Zm6.2004 -8.6169 3.4236 10.271c0.0131 0.0363 0.0253 0.073 0.0367 0.11l1.9344 5.8031c0.4366 1.3099 -0.2713 2.7257 -1.5811 3.1623 -1.3099 0.4366 -2.7257 -0.2713 -3.1623 -1.5811l-1.3828 -4.1484H8.68267l-1.38279 4.1484c-0.43662 1.3098 -1.85242 2.0177 -3.16228 1.5811 -1.30986 -0.4366 -2.01776 -1.8524 -1.58114 -3.1623l1.93438 -5.8031c0.01139 -0.037 0.02363 -0.0737 0.03668 -0.11l3.42365 -10.271c0.61685 -1.8505 2.34863 -3.0987 4.29923 -3.0987 1.9507 0 3.6825 1.2482 4.2993 3.0987ZM27.5833 6.89682c0.037 1.57858 0.7815 2.59014 1.7558 2.66428 1.2415 0.09449 3.4318 0.18841 7.2336 0.18841s5.9921 -0.09392 7.2336 -0.18841c0.9743 -0.07414 1.7188 -1.0857 1.7558 -2.66428 0.0067 -0.28582 0.0106 -0.60164 0.0106 -0.95004 0 -0.3484 -0.0039 -0.66423 -0.0106 -0.95004 -0.037 -1.57858 -0.7815 -2.59014 -1.7558 -2.66429 -1.2415 -0.09449 -3.4318 -0.18841 -7.2336 -0.18841s-5.9921 0.09392 -7.2336 0.18841c-0.9743 0.07415 -1.7188 1.08571 -1.7558 2.66429 -0.0067 0.28581 -0.0106 0.60164 -0.0106 0.95004 0 0.3484 0.0039 0.66422 0.0106 0.95004Zm1.7558 26.76978c-0.9743 -0.0742 -1.7188 -1.0857 -1.7558 -2.6643 -0.0067 -0.2858 -0.0106 -0.6017 -0.0106 -0.9501 0 -0.3484 0.0039 -0.6642 0.0106 -0.95 0.037 -1.5786 0.7815 -2.5901 1.7558 -2.6643 1.2415 -0.0945 3.4318 -0.1884 7.2336 -0.1884s5.9921 0.0939 7.2336 0.1884c0.9743 0.0742 1.7188 1.0857 1.7558 2.6643 0.0067 0.2858 0.0106 0.6016 0.0106 0.95 0 0.3484 -0.0039 0.6643 -0.0106 0.9501 -0.037 1.5786 -0.7815 2.5901 -1.7558 2.6643 -1.2415 0.0945 -3.4318 0.1884 -7.2336 0.1884s-5.9921 -0.0939 -7.2336 -0.1884Zm-1.7558 9.3357c0.037 1.5786 0.7815 2.5901 1.7558 2.6643 1.2415 0.0945 3.4318 0.1884 7.2336 0.1884s5.9921 -0.0939 7.2336 -0.1884c0.9743 -0.0742 1.7188 -1.0857 1.7558 -2.6643 0.0067 -0.2858 0.0106 -0.6017 0.0106 -0.9501 0 -0.3484 -0.0039 -0.6642 -0.0106 -0.95 -0.037 -1.5786 -0.7815 -2.5901 -1.7558 -2.6643 -1.2415 -0.0945 -3.4318 -0.1884 -7.2336 -0.1884s-5.9921 0.0939 -7.2336 0.1884c-0.9743 0.0742 -1.7188 1.0857 -1.7558 2.6643 -0.0067 0.2858 -0.0106 0.6016 -0.0106 0.95 0 0.3484 0.0039 0.6643 0.0106 0.9501Zm1.7558 -21.3357c-0.9743 -0.0742 -1.7188 -1.0857 -1.7558 -2.6643 -0.0067 -0.2858 -0.0106 -0.6017 -0.0106 -0.9501 0 -0.3484 0.0039 -0.6642 0.0106 -0.95 0.037 -1.5786 0.7815 -2.5901 1.7558 -2.6643 1.2415 -0.0945 3.4318 -0.1884 7.2336 -0.1884s5.9921 0.0939 7.2336 0.1884c0.9743 0.0742 1.7188 1.0857 1.7558 2.6643 0.0067 0.2858 0.0106 0.6016 0.0106 0.95 0 0.3484 -0.0039 0.6643 -0.0106 0.9501 -0.037 1.5786 -0.7815 2.5901 -1.7558 2.6643 -1.2415 0.0945 -3.4318 0.1884 -7.2336 0.1884s-5.9921 -0.0939 -7.2336 -0.1884Z" clip-rule="evenodd" stroke-width="1"></path>
          </svg>
          </button>

          <div class="flex items-center rounded border border-white/15 overflow-hidden">
            <button
              class="h-7 px-2.5 text-xs font-bold tracking-tight cursor-pointer transition-colors flex items-center gap-1.5"
              :class="viewMode === 'list'
                ? 'bg-blue-700 text-white! shadow-sm'
                : 'text-white hover:bg-slate-700'"
              @click="viewMode = 'list'"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48" id="Justified-Alignment--Streamline-Plump" height="48" width="48" class="h-4 w-4">
                <g id="Solid/Interface Essential/Text Formatting/justified-alignment--justified-align-alignment-paragraph-formatting-text">
                  <path id="Union" fill="currentColor" fill-rule="evenodd" d="M4.83941 3.2097C7.05175 3.12767 12.3623 3 24 3c11.6377 0 16.9483 0.12767 19.1606 0.2097 1.1428 0.04237 2.5331 0.67172 2.754 2.22165 0.0527 0.36968 0.0854 0.80562 0.0854 1.31865 0 0.51303 -0.0327 0.94897 -0.0854 1.31865 -0.2209 1.54993 -1.6112 2.17925 -2.754 2.22165 -2.2124 0.082 -7.5229 0.2097 -19.1606 0.2097 -11.6377 0 -16.94825 -0.1277 -19.16059 -0.2097 -1.1428 -0.0424 -2.53312 -0.67172 -2.75401 -2.22165C2.03271 7.69897 2 7.26304 2 6.75s0.03271 -0.94897 0.0854 -1.31865c0.22089 -1.54992 1.61122 -2.17928 2.75401 -2.22165Zm0 11.5C7.05175 14.6277 12.3623 14.5 24 14.5c11.6377 0 16.9483 0.1277 19.1606 0.2097 1.1428 0.0424 2.5331 0.6717 2.754 2.2216 0.0527 0.3697 0.0854 0.8057 0.0854 1.3187s-0.0327 0.949 -0.0854 1.3187c-0.2209 1.5499 -1.6112 2.1792 -2.754 2.2216C40.9482 21.8723 35.6377 22 24 22c-11.6377 0 -16.94825 -0.1277 -19.16059 -0.2097 -1.14279 -0.0424 -2.53312 -0.6717 -2.75401 -2.2217C2.03271 19.199 2 18.763 2 18.25s0.03271 -0.949 0.0854 -1.3186c0.22089 -1.55 1.61121 -2.1793 2.75401 -2.2217ZM24 26c-11.6377 0 -16.94825 0.1277 -19.16059 0.2097 -1.1428 0.0424 -2.53312 0.6717 -2.75401 2.2217C2.03271 28.801 2 29.237 2 29.75s0.03271 0.949 0.0854 1.3186c0.22089 1.55 1.61121 2.1793 2.75401 2.2217C7.05175 33.3723 12.3623 33.5 24 33.5c11.6377 0 16.9482 -0.1277 19.1606 -0.2097 1.1428 -0.0424 2.5331 -0.6717 2.754 -2.2216 0.0527 -0.3697 0.0854 -0.8057 0.0854 -1.3187s-0.0327 -0.949 -0.0854 -1.3187c-0.2209 -1.5499 -1.6112 -2.1792 -2.754 -2.2216C40.9483 26.1277 35.6377 26 24 26ZM4.83941 37.7097C7.05175 37.6277 12.3623 37.5 24 37.5c11.6377 0 16.9483 0.1277 19.1606 0.2097 1.1428 0.0424 2.5331 0.6717 2.754 2.2216 0.0527 0.3697 0.0854 0.8057 0.0854 1.3187s-0.0327 0.949 -0.0854 1.3187c-0.2209 1.5499 -1.6112 2.1792 -2.754 2.2216C40.9482 44.8723 35.6377 45 24 45c-11.6377 0 -16.94825 -0.1277 -19.16059 -0.2097 -1.14279 -0.0424 -2.53312 -0.6717 -2.75401 -2.2216C2.03271 42.199 2 41.763 2 41.25s0.03271 -0.949 0.0854 -1.3186c0.22089 -1.55 1.61121 -2.1793 2.75401 -2.2217Z" clip-rule="evenodd" stroke-width="1"></path>
                </g>
              </svg>
            </button>
            <button
              class="h-7 px-2.5 text-xs font-bold tracking-tight cursor-pointer transition-colors flex items-center gap-1.5"
              :class="viewMode === 'matrix'
                ? 'bg-blue-700 text-white! shadow-sm'
                : 'text-white hover:bg-slate-700'"
              @click="viewMode = 'matrix'"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48" id="Application-Drawer--Streamline-Plump" height="48" width="48" class="w-4 h-4">
                <g id="application-drawer">
                  <path id="Union" fill="currentColor" fill-rule="evenodd" d="M8 1.5c0.74245 0 1.39483 0.01156 1.96206 0.0298 2.49884 0.08035 4.42774 2.00927 4.50814 4.50814C14.4884 6.60517 14.5 7.25755 14.5 8c0 0.74245 -0.0116 1.39484 -0.0298 1.96206 -0.0804 2.49884 -2.0093 4.42784 -4.50814 4.50814C9.39483 14.4884 8.74245 14.5 8 14.5c-0.74245 0 -1.39484 -0.0116 -1.96206 -0.0298 -2.49887 -0.0803 -4.42779 -2.0093 -4.50814 -4.50813C1.51156 9.39484 1.5 8.74245 1.5 8s0.01156 -1.39484 0.0298 -1.96206c0.08035 -2.49887 2.00927 -4.42779 4.50814 -4.50814C6.60517 1.51156 7.25755 1.5 8 1.5Zm0 16c0.74245 0 1.39483 0.0116 1.96206 0.0298 2.49884 0.0804 4.42774 2.0093 4.50814 4.5081 0.0182 0.5673 0.0298 1.2197 0.0298 1.9621 0 0.7425 -0.0116 1.3948 -0.0298 1.9621 -0.0804 2.4988 -2.0093 4.4278 -4.50814 4.5081C9.39483 30.4884 8.74245 30.5 8 30.5c-0.74245 0 -1.39484 -0.0116 -1.96206 -0.0298 -2.49887 -0.0803 -4.42779 -2.0093 -4.50814 -4.5081C1.51156 25.3948 1.5 24.7425 1.5 24c0 -0.7424 0.01156 -1.3948 0.0298 -1.9621 0.08035 -2.4988 2.00927 -4.4277 4.50814 -4.5081C6.60517 17.5116 7.25755 17.5 8 17.5Zm1.96206 16.0298C9.39483 33.5116 8.74245 33.5 8 33.5c-0.74245 0 -1.39483 0.0116 -1.96206 0.0298 -2.49887 0.0804 -4.42779 2.0093 -4.50814 4.5081C1.51156 38.6052 1.5 39.2576 1.5 40c0 0.7425 0.01156 1.3948 0.0298 1.9621 0.08035 2.4988 2.00927 4.4278 4.50814 4.5081C6.60516 46.4884 7.25755 46.5 8 46.5c0.74245 0 1.39483 -0.0116 1.96206 -0.0298 2.49884 -0.0803 4.42774 -2.0093 4.50814 -4.5081 0.0182 -0.5673 0.0298 -1.2196 0.0298 -1.9621 0 -0.7424 -0.0116 -1.3948 -0.0298 -1.9621 -0.0804 -2.4988 -2.0093 -4.4277 -4.50814 -4.5081ZM24 1.5c0.7424 0 1.3948 0.01156 1.9621 0.0298 2.4988 0.08035 4.4277 2.00927 4.5081 4.50814C30.4884 6.60517 30.5 7.25755 30.5 8c0 0.74245 -0.0116 1.39484 -0.0298 1.96206 -0.0804 2.49884 -2.0093 4.42784 -4.5081 4.50814 -0.5673 0.0182 -1.2197 0.0298 -1.9621 0.0298 -0.7425 0 -1.3948 -0.0116 -1.9621 -0.0298 -2.4988 -0.0803 -4.4277 -2.0093 -4.5081 -4.50813C17.5116 9.39484 17.5 8.74245 17.5 8s0.0116 -1.39484 0.0298 -1.96206c0.0804 -2.49887 2.0093 -4.42779 4.5081 -4.50814C22.6052 1.51156 23.2576 1.5 24 1.5Zm1.9621 16.0298C25.3948 17.5116 24.7424 17.5 24 17.5s-1.3948 0.0116 -1.9621 0.0298c-2.4988 0.0804 -4.4277 2.0093 -4.5081 4.5081 -0.0182 0.5673 -0.0298 1.2197 -0.0298 1.9621 0 0.7425 0.0116 1.3948 0.0298 1.9621 0.0804 2.4988 2.0093 4.4278 4.5081 4.5081 0.5673 0.0182 1.2196 0.0298 1.9621 0.0298 0.7424 0 1.3948 -0.0116 1.9621 -0.0298 2.4988 -0.0803 4.4277 -2.0093 4.5081 -4.5081 0.0182 -0.5673 0.0298 -1.2196 0.0298 -1.9621 0 -0.7424 -0.0116 -1.3948 -0.0298 -1.9621 -0.0804 -2.4988 -2.0093 -4.4277 -4.5081 -4.5081ZM24 33.5c0.7424 0 1.3948 0.0116 1.9621 0.0298 2.4988 0.0804 4.4277 2.0093 4.5081 4.5081 0.0182 0.5673 0.0298 1.2197 0.0298 1.9621 0 0.7425 -0.0116 1.3948 -0.0298 1.9621 -0.0804 2.4988 -2.0093 4.4278 -4.5081 4.5081 -0.5673 0.0182 -1.2197 0.0298 -1.9621 0.0298 -0.7425 0 -1.3948 -0.0116 -1.9621 -0.0298 -2.4988 -0.0803 -4.4277 -2.0093 -4.5081 -4.5081C17.5116 41.3948 17.5 40.7425 17.5 40c0 -0.7424 0.0116 -1.3948 0.0298 -1.9621 0.0804 -2.4988 2.0093 -4.4277 4.5081 -4.5081 0.5673 -0.0182 1.2197 -0.0298 1.9621 -0.0298ZM41.9621 1.5298C41.3948 1.51156 40.7425 1.5 40 1.5c-0.7424 0 -1.3948 0.01156 -1.9621 0.0298 -2.4988 0.08035 -4.4277 2.00927 -4.5081 4.50814C33.5116 6.60516 33.5 7.25755 33.5 8c0 0.74245 0.0116 1.39484 0.0298 1.96207 0.0803 2.49883 2.0093 4.42783 4.5081 4.50813 0.5673 0.0182 1.2196 0.0298 1.9621 0.0298 0.7424 0 1.3948 -0.0116 1.9621 -0.0298 2.4988 -0.0803 4.4277 -2.0093 4.5081 -4.50814C46.4884 9.39484 46.5 8.74245 46.5 8c0 -0.74245 -0.0116 -1.39483 -0.0298 -1.96206 -0.0803 -2.49887 -2.0093 -4.42779 -4.5081 -4.50814ZM40 17.5c0.7425 0 1.3948 0.0116 1.9621 0.0298 2.4988 0.0804 4.4278 2.0093 4.5081 4.5081 0.0182 0.5673 0.0298 1.2197 0.0298 1.9621 0 0.7425 -0.0116 1.3948 -0.0298 1.9621 -0.0804 2.4988 -2.0093 4.4278 -4.5081 4.5081 -0.5673 0.0182 -1.2197 0.0298 -1.9621 0.0298 -0.7425 0 -1.3948 -0.0116 -1.9621 -0.0298 -2.4988 -0.0803 -4.4278 -2.0093 -4.5081 -4.5081C33.5116 25.3948 33.5 24.7425 33.5 24c0 -0.7424 0.0116 -1.3948 0.0298 -1.9621 0.0804 -2.4988 2.0093 -4.4277 4.5081 -4.5081 0.5673 -0.0182 1.2197 -0.0298 1.9621 -0.0298Zm1.9621 16.0298C41.3948 33.5116 40.7425 33.5 40 33.5c-0.7424 0 -1.3948 0.0116 -1.9621 0.0298 -2.4988 0.0804 -4.4277 2.0093 -4.5081 4.5081 -0.0182 0.5673 -0.0298 1.2197 -0.0298 1.9621 0 0.7425 0.0116 1.3948 0.0298 1.9621 0.0803 2.4988 2.0093 4.4278 4.5081 4.5081 0.5673 0.0182 1.2196 0.0298 1.9621 0.0298 0.7424 0 1.3948 -0.0116 1.9621 -0.0298 2.4988 -0.0803 4.4277 -2.0093 4.5081 -4.5081 0.0182 -0.5673 0.0298 -1.2196 0.0298 -1.9621 0 -0.7424 -0.0116 -1.3948 -0.0298 -1.9621 -0.0803 -2.4988 -2.0093 -4.4277 -4.5081 -4.5081Z" clip-rule="evenodd" stroke-width="1"></path>
                </g>
              </svg>
            </button>
          </div>

          <!-- Health Filter -->
          <div class="flex items-center rounded border border-white/15 overflow-hidden">
            <button
              v-for="f in healthFilters"
              :key="f.value"
              class="px-3 py-1 h-7 text-xs font-bold tracking-tight cursor-pointer transition-colors"
              :class="healthFilter === f.value
                ? 'bg-blue-700 text-white! shadow-sm'
                : 'text-white hover:bg-slate-700'"
              @click="healthFilter = f.value"
            >
              {{ f.label }}
            </button>
          </div>

          <!-- Live Filter -->
          <button
            class="h-7 px-3 text-xs font-bold tracking-tight border rounded cursor-pointer transition-colors"
            :class="liveOnly
              ? 'bg-red-700 border-red-600! text-white! shadow-sm'
              : 'border-white/15 text-white hover:bg-slate-700'"
            @click="liveOnly = !liveOnly"
          >
            Live
          </button>

          <!-- Refresh -->
          <button
            class="h-8 px-2 text-xs flex items-center text-slate-400 hover:text-white border border-white/15 rounded cursor-pointer transition-colors"
            :disabled="loading || loadingStatuses || loadingLiveAssignments"
            @click="refresh"
          >
            <i class="bi bly-loading" :class="{ 'animate-spin': loading || loadingStatuses || loadingLiveAssignments }"></i>
          </button>
        </div>

        <div class="flex items-center gap-2 md:hidden! ml-auto">
          <button
            class="h-8 w-8 flex items-center justify-center text-slate-400 hover:text-white border border-white/10 rounded cursor-pointer transition-colors"
            :disabled="loading || loadingStatuses || loadingLiveAssignments"
            @click="refresh"
          >
            <i class="bi bly-loading" :class="{ 'animate-spin': loading || loadingStatuses || loadingLiveAssignments }"></i>
          </button>
          <button
            class="relative h-8 w-8 flex items-center justify-center border rounded cursor-pointer transition-colors"
            :class="mobileFiltersActive
              ? 'border-blue-500 bg-blue-500/10 text-blue-300'
              : 'border-white/10 text-slate-300 hover:bg-slate-800'"
            title="Open filters"
            @click="openFiltersDrawer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48" id="Filter-2--Streamline-Plump" height="48" width="48" class="w-3 h-3">
              <g id="Solid/Interface Essential/Text Formatting/filter-2--funnel-filter-angle-oil">
                <path id="Union" fill="currentColor" d="M2.07102 14.8983c2.12634 2.0816 7.06468 6.6703 13.62318 11.1919 0.136 0.0937 0.2177 0.2396 0.23 0.39 0.7251 8.8677 1.3948 15.1249 1.7178 17.9733 0.198 1.7455 1.6761 3.0465 3.4187 3.0465 0.6976 0 1.3787 -0.2111 1.954 -0.6056l5.256 -3.6041c1.1037 -0.7568 1.9215 -1.9165 2.1674 -3.305 0.3534 -1.9954 0.964 -6.1024 1.6196 -13.4924 0.0136 -0.1526 0.097 -0.2986 0.2309 -0.3909 6.5671 -4.5254 11.5122 -9.1203 13.6404 -11.2037 1.0366 -1.0149 1.571 -2.3897 1.571 -3.793V4.72118c0 -1.64877 -1.1727 -3.13325 -2.9129 -3.34996C41.9093 1.03775 35.7696 0.5 24 0.5c-11.7696 0 -17.90927 0.53775 -20.58709 0.87122C1.67269 1.58793 0.499997 3.07241 0.5 4.72119l0.00001 6.38411c-0.000008 1.4033 0.53435 2.7781 1.57101 3.793Z" stroke-width="1"></path>
              </g>
            </svg>
            <span
              v-if="mobileFiltersActive"
              class="absolute -right-1 -top-1 min-w-4 h-4 px-1 rounded-full bg-blue-500 text-[9px] font-bold text-white flex items-center justify-center"
            >
              {{ mobileFiltersCount }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <transition name="source-monitoring-drawer">
      <div
        v-if="isFiltersDrawerOpen"
        class="fixed inset-0 z-50 md:hidden top-12"
      >
        <button
          class="absolute inset-0 bg-slate-950/75 backdrop-blur-[1px]"
          aria-label="Close filters"
          @click="closeFiltersDrawer"
        ></button>

        <div class="absolute right-0 top-0 h-full w-full max-w-sm overflow-y-auto bg-[#13191d] shadow-2xl">
          <div class="flex items-center justify-between px-5 py-4">
            <div>
              <p class="text-sm font-semibold text-slate-100">Filters</p>
              <p class="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                {{ activeTab }}
              </p>
            </div>
            <button
              class="h-9 w-9 flex items-center justify-center rounded  text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
              aria-label="Close filters"
              @click="closeFiltersDrawer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48" id="Delete-1--Streamline-Plump" height="48" width="48" class="w-3 h-3">
                <g id="Solid/Interface Essential/Add Remove Delete/delete-1--remove-add-button-buttons-delete-cross-x-mathematics-multiply-math">
                  <path id="Union" fill="currentColor" d="M44.7021 0.7704c-0.6868 -0.3728 -1.3995 -0.3517 -2.035 -0.1323 -0.6219 0.2148 -1.1856 0.6222 -1.6463 1.0605 -2.5083 2.387 -8.5323 8.1658 -17.0209 16.6073C15.5115 9.8644 9.4874 4.0856 6.9791 1.6986 6.5186 1.2603 5.9548 0.8529 5.3329 0.6381 4.6974 0.4187 3.9847 0.3976 3.2979 0.7704c-0.4425 0.2401 -0.9254 0.5908 -1.431 1.0964 -0.5055 0.5055 -0.8562 0.9885 -1.0964 1.4309 -0.3728 0.6868 -0.3517 1.3996 -0.1322 2.0351 0.2147 0.6219 0.6222 1.1856 1.0605 1.6462 2.3869 2.5083 8.1657 8.5324 16.6073 17.0209C9.8644 32.4886 4.0854 38.5127 1.6984 41.0211c-0.4382 0.4605 -0.8457 1.0242 -1.0604 1.6461 -0.2195 0.6356 -0.2406 1.3483 0.1322 2.0351 0.2402 0.4424 0.5909 0.9254 1.0964 1.431 0.5056 0.5054 0.9885 0.8561 1.431 1.0964 0.6868 0.3727 1.3995 0.3516 2.035 0.1322 0.6219 -0.2148 1.1856 -0.6222 1.6462 -1.0605C9.4871 43.9144 15.5113 38.1356 24 29.6938c8.4887 8.4418 14.5129 14.2206 17.0212 16.6076 0.4605 0.4383 1.0242 0.8457 1.6461 1.0605 0.6356 0.2194 1.3483 0.2405 2.0351 -0.1322 0.4424 -0.2403 0.9254 -0.591 1.431 -1.0964 0.5055 -0.5056 0.8562 -0.9886 1.0964 -1.431 0.3728 -0.6868 0.3517 -1.3995 0.1322 -2.0351 -0.2147 -0.6219 -0.6222 -1.1856 -1.0605 -1.6461 -2.387 -2.5084 -8.1658 -8.5325 -16.6076 -17.0212C38.1354 15.5114 43.9143 9.4873 46.3012 6.979c0.4384 -0.4606 0.8457 -1.0243 1.0605 -1.6462 0.2195 -0.6355 0.2406 -1.3483 -0.1323 -2.0351 -0.2401 -0.4424 -0.5908 -0.9254 -1.0964 -1.4309 -0.5055 -0.5055 -0.9884 -0.8562 -1.4309 -1.0964Z" stroke-width="1"></path>
                </g>
              </svg>
            </button>
          </div>

          <div class="space-y-5 px-5 py-5">
            <div class="space-y-2">
              <label class="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Search
              </label>
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="activeTab === 'inputs' ? 'Search inputs...' : 'Search outputs...'"
                class="h-10 w-full rounded border border-white/10 bg-[#13191d] px-3 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>

            <div class="space-y-2">
              <label class="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Type
              </label>
              <div class="relative">
                <select
                  v-model="selectedType"
                  class="h-10 w-full appearance-none rounded border border-white/10 bg-[#13191d] pl-3 pr-10 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                >
                  <option value="" class="bg-[#13191d] text-slate-200">All Types</option>
                  <option
                    v-for="t in currentAvailableTypes"
                    :key="t"
                    :value="t"
                    class="bg-slate-800 text-slate-200"
                  >
                    {{ t }}
                  </option>
                </select>
                <i class="bi bi-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none"></i>
              </div>
            </div>

            <div class="space-y-2">
              <label class="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Sort
              </label>
              <button
                class="flex h-10 w-full items-center justify-between rounded border border-white/10 bg-[#13191d] px-3 text-sm font-semibold text-slate-200 transition-colors hover:border-white/20 hover:bg-slate-800"
                @click="toggleSortBy"
              >
                <span class="inline-flex items-center gap-2">
                  <i class="bi bi-arrow-down-up text-xs text-slate-400"></i>
                  <span>{{ sortButtonLabel }}</span>
                </span>
                <span class="text-[11px] uppercase tracking-[0.16em] text-slate-500">Tap to switch</span>
              </button>
            </div>

            <div class="space-y-2">
              <label class="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                View
              </label>
              <div class="grid grid-cols-2 gap-2">
                <button
                  class="h-10 rounded border text-xs font-bold tracking-tight transition-colors flex items-center justify-center gap-2"
                  :class="viewMode === 'list'
                    ? 'border-yellow-500 bg-yellow-700 text-black!'
                    : 'border-white/10 bg-[#13191d] text-slate-200 hover:bg-slate-800'"
                  @click="viewMode = 'list'"
                >
                  <i class="bi bi-list-ul text-[11px]"></i>
                  <span>List</span>
                </button>
                <button
                  class="h-10 rounded border text-xs font-bold tracking-tight transition-colors flex items-center justify-center gap-2"
                  :class="viewMode === 'matrix'
                    ? 'border-yellow-500 bg-yellow-700 text-black!'
                    : 'border-white/10 bg-[#13191d] text-slate-200 hover:bg-slate-800'"
                  @click="viewMode = 'matrix'"
                >
                  <i class="bi bi-grid-3x3-gap text-[11px]"></i>
                  <span>Matrix</span>
                </button>
              </div>
            </div>

            <div class="space-y-2">
              <label class="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Health
              </label>
              <div class="grid grid-cols-2 gap-2">
                <button
                  v-for="f in healthFilters"
                  :key="`drawer-${f.value}`"
                  class="h-10 rounded border text-xs font-bold tracking-tight transition-colors"
                  :class="healthFilter === f.value
                    ? 'border-yellow-500 bg-yellow-700 text-black!'
                    : 'border-white/10 bg-[#13191d] text-slate-200 hover:bg-slate-800'"
                  @click="healthFilter = f.value"
                >
                  {{ f.label }}
                </button>
              </div>
            </div>

            <div class="space-y-2">
              <label class="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Live
              </label>
              <button
                class="flex h-10 w-full items-center justify-between rounded border px-3 text-sm font-semibold transition-colors"
                :class="liveOnly
                  ? 'border-red-500 bg-red-700/20 text-red-300'
                  : 'border-white/10 bg-[#13191d] text-slate-200 hover:bg-slate-800'"
                @click="liveOnly = !liveOnly"
              >
                <span>Show live only</span>
                <span class="text-[11px] uppercase tracking-[0.16em]">
                  {{ liveOnly ? 'On' : 'Off' }}
                </span>
              </button>
            </div>

            <div class="flex items-center gap-2 pt-2">
              <button
                class="flex h-10 flex-1 items-center justify-center gap-2 rounded border border-white/10 bg-[#13191d] text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-800"
                @click="resetCurrentTabFilters"
              >
                Reset
              </button>
              <button
                class="flex h-10 flex-1 items-center justify-center gap-2 rounded bg-yellow-600 text-sm font-semibold text-black! transition-colors hover:bg-yellow-500"
                @click="closeFiltersDrawer"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Input List -->
    <div v-if="activeTab === 'inputs'" v-bar class="flex-1">
      <div class="p-4">
        <div v-if="loading && inputs.length === 0" class="text-center text-slate-500 py-12">
          Loading inputs...
        </div>

        <div v-else-if="filteredInputs.length === 0" class="text-center text-slate-500 py-12">
          No inputs found
        </div>

        <div
          v-else-if="viewMode === 'matrix'"
          class="grid gap-4 sm:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-7"
        >
          <button
            v-for="input in filteredInputs"
            :key="`matrix-${input.id}`"
            class="group overflow-hidden rounded-sm bg-slate-800/50 border-none border-slate-700/40 hover:border-slate-600/60 transition-colors text-left  hover:shadow-black/30"
            @click="openMatrixDetails(input)"
          >
            <div class="relative aspect-video overflow-hidden bg-slate-900/80">
              <img
                v-if="getThumbnailData(input).data && !getThumbnailData(input).error && !getThumbnailData(input).warning"
                :src="getThumbnailData(input).data"
                :alt="`${input.name} thumbnail`"
                class="h-full w-full object-cover transition-transform duration-500"
              />
              <div
                v-else-if="getThumbnailData(input).loading"
                class="flex h-full w-full items-center justify-center text-slate-400"
              >
                <i class="bi bly-loading animate-spin text-lg"></i>
              </div>
              <div
                v-else-if="getThumbnailData(input).warning"
                class="flex h-full w-full items-center justify-center text-yellow-400"
              >
                <el-tooltip :content="getThumbnailData(input).warning" placement="top">
                  <i class="bi bly-warning text-lg"></i>
                </el-tooltip>
              </div>
              <div
                v-else-if="getThumbnailData(input).error"
                class="flex h-full w-full items-center justify-center text-red-400"
              >
                <i class="bi bly-alert text-lg"></i>
              </div>
              <div
                v-else
                class="flex h-full w-full items-center justify-center text-slate-500"
              >
                <i class="bi bly-image text-2xl"></i>
              </div>

              <div class="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent"></div>

              <div class="absolute left-3 top-3 flex items-center gap-2">
                <span class="flex items-center gap-1 rounded-sm bg-slate-950/75 px-1 h-4 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-100">
                  <span class="size-2 rounded-full" :class="statusDotClass(input)"></span>
                  {{ statusLabel(input) }} 
                </span>
                <span
                  v-if="isInputLive(input.id)"
                  class="rounded-full bg-red-600/85 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white"
                >
                  Live
                </span>
              </div>

              <span class="absolute right-3 top-3 rounded-sm bg-slate-950/75 px-1 h-4 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-200">
                {{ input.type }}
              </span>

              <div class="absolute inset-x-3 bottom-3 flex-col text-[10px]">
                <p class="truncate text-xs font-semibold text-white">{{ input.name }}</p>
                <p class="mt-1 truncate text-[10px] text-slate-300/85">
                  {{ input.location ? parseLocation(input.location) : 'No location assigned' }}
                </p>
              </div>
            </div>

            <div class="space-y-3 p-4 hidden">
              <div class="flex flex-wrap gap-2">
                <span
                  v-if="getProtocol(input)"
                  class="rounded-full border border-white/10 bg-slate-800/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-300"
                >
                  {{ getProtocol(input) }}
                </span>
                <span
                  class="rounded-full border border-white/10 bg-slate-800/40 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400"
                >
                  {{ getInputState(input.id) || 'UNKNOWN' }}
                </span>
              </div>

              <div class="space-y-1">
                <p class="truncate text-sm text-slate-200">{{ matrixInputSummary(input) }}</p>
                <p class="truncate text-xs uppercase tracking-[0.16em] text-slate-500">{{ matrixInputMeta(input) }}</p>
              </div>

              <div class="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                <span>{{ statusTooltip(input) }}</span>
                <span class="text-blue-300 transition-colors group-hover:text-blue-200">Open details</span>
              </div>
            </div>
          </button>
        </div>

        <div v-else class="grid gap-2">
          <div
            v-for="input in filteredInputs"
            :key="input.id"
            class="flex items-center gap-4 pl-1 pr-4 py-1 rounded bg-slate-800/50 border-none border-slate-700/40 hover:border-slate-600/60 transition-colors"
          >
            <!-- Thumbnail -->
            <div class="h-12 w-20 shrink-0 overflow-hidden rounded bg-slate-900/70 border border-slate-700/40 flex items-center justify-center">
              <img
                v-if="getThumbnailData(input).data && !getThumbnailData(input).error && !getThumbnailData(input).warning"
                :src="getThumbnailData(input).data"
                :alt="`${input.name} thumbnail`"
                class="h-full w-full object-cover"
                :title="thumbnailTooltip(input)"
              />
              <div
                v-else-if="getThumbnailData(input).loading"
                class="flex items-center justify-center text-slate-400"
              >
                <i class="bi bly-loading animate-spin text-xs"></i>
              </div>
              <div
                v-else-if="getThumbnailData(input).warning"
                class="flex items-center justify-center text-yellow-400"
              >
                <el-tooltip :content="getThumbnailData(input).warning" placement="top">
                  <i class="bi bly-warning text-xs"></i>
                </el-tooltip>
              </div>
              <div
                v-else-if="getThumbnailData(input).error"
                class="flex items-center justify-center text-red-400"
                :title="getThumbnailData(input).error"
              >
                <i class="bi bly-alert text-xs"></i>
              </div>
              <div
                v-else
                class="flex items-center justify-center text-slate-500"
                title="No thumbnail"
              >
                <i class="bi bly-image text-xs"></i>
              </div>
            </div>

            <!-- Status Indicator -->
            <div
              class="size-2.5 rounded-full shrink-0"
              :class="statusDotClass(input)"
              :title="statusTooltip(input)"
            ></div>

            <!-- Input Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-sm text-slate-200 truncate">{{ input.name }}  - {{ input.id }}</span>
                <span class="text-[10px] px-1.5 py-0.5 h-4 rounded uppercase tracking-wide shrink-0" :class="inputTypeBadgeClass(input.type)">
                  {{ input.type }}
                </span>
                <span v-if="input.location" class="text-[10px] px-1.5 py-0.5 h-4 rounded bg-slate-700/40 text-slate-500 shrink-0">
                  {{ parseLocation(input.location) }}
                </span>
              </div>

              <!-- Info details (MEDIA_CONNECT only) -->
              <div v-if="getInputInfo(input.id) && input.type === 'MEDIA_CONNECT'" class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                <!-- Protocol/Type -->
                <span v-if="getInputInfo(input.id).Configuration" class="itemss-center flex text-[10px] px-1.5 h-4 rounded bg-slate-800/60 text-slate-400 border border-slate-700/30">
                  {{ getProtocol(input) }}
                </span>

                <span class="text-slate-700 select-none">|</span>

                <!-- Source Status -->
                <span
                  v-for="source in getMediaConnectSources(input)"
                  :key="`${input.id}-${source.label}`"
                  class="flex items-center gap-1.5 px-1.5 h-4 rounded border"
                  :class="source.connected
                    ? 'bg-green-900/20 text-green-400 border-green-800/20'
                    : 'bg-red-900/20 text-red-400 border-red-800/20'"
                >
                  <span class="text-[9px] font-bold opacity-50">{{ source.label }}</span>
                  <span v-if="source.connected" class="font-mono">{{ source.ip }}</span>
                  <span v-else class="italic text-[10px]">Not Connected</span>
                </span>
                <span
                  class="flex items-center gap-1.5 px-2 h-4 rounded border text-[10px] font-semibold uppercase tracking-[0.12em]"
                  :class="connectionStatusClass(input)"
                >
                  {{ connectionStatusLabel(input) }}
                </span>

                <!-- Outputs -->
                <span v-if="getInputInfo(input.id).RoutedOutputs != null" class="flex items-center gap-1.5 px-2 py-0.5 h-4 rounded bg-slate-800/40 text-slate-400 border border-slate-700/30">
                  <i class="bi bi-box-arrow-right text-[10px]"></i>
                  <span>{{ getInputInfo(input.id).RoutedOutputs }} <span class="text-[9px] opacity-50">OUTPUTS</span></span>
                </span>

                <a
                  v-if="getInputInfo(input.id).Name"
                  :href="getGrafanaUrl(getInputInfo(input.id).Name)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-400 hover:text-blue-200 transition-colors flex items-center gap-1 bg-blue-400/10 px-2 py-0.5 h-4 rounded border border-blue-400/20"
                  @click.stop
                >
                  <i class="bi bi-graph-up text-[10px]"></i>
                  Metrics ↗
                </a>
              </div>

              <!-- Info details (ROUTE only) -->
              <div v-if="getInputInfo(input.id) && input.type === 'ROUTE'" class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                <span v-if="getInputInfo(input.id).source?.url" class="flex text-[10px] items-center gap-1 px-1.5 py-0.5 h-4 rounded bg-blue-900/20 text-blue-300/80 border border-blue-800/20">
                  <span class="text-[9px] font-bold opacity-50">SRC</span>
                  <span class="font-mono">{{ getInputInfo(input.id).source.url }}</span>
                  <span class="text-[9px] opacity-40 ml-1">({{ getInputInfo(input.id).source.type }})</span>
                </span>

                <span class="text-slate-700 select-none">|</span>

                <span v-if="getInputInfo(input.id).destination?.url" class="flex text-[10px] items-center gap-1 px-1.5 py-0.5 h-4 rounded bg-indigo-900/20 text-indigo-300/80 border border-indigo-800/20">
                  <span class="text-[9px] font-bold opacity-50">DST</span>
                  <span class="font-mono">{{ getInputInfo(input.id).destination.url }}</span>
                </span>

                <span v-if="getInputInfo(input.id).connectedClients != null" class="flex items-center gap-1.5 px-2 py-0.5 h-4 rounded bg-slate-800/40 text-slate-400 border border-slate-700/30">
                  <i class="bi bi-people text-[10px]"></i>
                  <span>{{ getInputInfo(input.id).connectedClients }} <span class="text-[9px] opacity-50">CLIENTS</span></span>
                </span>
              </div>

              <!-- Messages (errors/warnings) -->
              <div v-if="getInputMessages(input).length > 0" class="mt-1.5 flex flex-col gap-1">
                <div
                  v-for="(msg, idx) in getInputMessages(input)"
                  :key="idx"
                  class="text-xs flex items-start gap-1.5"
                  :class="messageClass(msg)"
                >
                  <span class="shrink-0 mt-0.5">{{ messageIcon(msg) }}</span>
                  <span>{{ msg.Message }}</span>
                </div>
              </div>
            </div>

            <!-- State + Health -->
            <div class="shrink-0 text-right flex flex-col items-end gap-1">
              <span
                v-if="getInputState(input.id)"
                class="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide"
                :class="getInputState(input.id) === 'ACTIVE'
                  ? 'bg-green-900/30 text-green-400'
                  : 'bg-slate-700/50 text-slate-400'"
              >
                {{ getInputState(input.id) }}
              </span>
              <span
                class="text-xs px-2 py-1 rounded"
                :class="statusLabelClass(input)"
              >
                {{ statusLabel(input) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Output List -->
    <div v-if="activeTab === 'outputs'" v-bar class="flex-1">
      <div class="p-4">
        <div v-if="loading && outputs.length === 0" class="text-center text-slate-500 py-12">
          Loading outputs...
        </div>

        <div v-else-if="filteredOutputs.length === 0" class="text-center text-slate-500 py-12">
          No outputs found
        </div>

        <div
          v-else-if="viewMode === 'matrix'"
          class="grid  gap-4 sm:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6"
        >
          <button
            v-for="output in filteredOutputs"
            :key="`matrix-${output.id}`"
            class="group overflow-hidden rounded-md border border-white/10 bg-[#0f1519] text-left transition-all duration-200 hover:-translate-y-1 hover:border-white/20 hover:shadow-2xl hover:shadow-black/30"
            @click="openMatrixDetails(output)"
          >
            <div class="relative aspect-video overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.22),_transparent_36%),linear-gradient(135deg,_rgba(15,23,42,0.95),_rgba(17,24,39,0.95))]">
              <div class="absolute inset-0 bg-[linear-gradient(135deg,transparent_0%,rgba(255,255,255,0.04)_35%,transparent_70%)]"></div>
              <div class="absolute right-[-18px] top-[-12px] h-28 w-28 rounded-full bg-orange-500/10 blur-2xl"></div>
              <div class="absolute left-3 top-3 flex items-center gap-2">
                <span class="flex items-center gap-1 rounded-sm bg-slate-950/75 px-2 h-4 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-100">
                  <span class="size-2 rounded-full" :class="outputStatusDotClass(output)"></span>
                  {{ outputStatusLabel(output) }}
                </span>
                <span
                  v-if="isOutputLive(output.id)"
                  class="rounded-full bg-red-600/85 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white"
                >
                  Live
                </span>
              </div>

              <span class="absolute right-3 top-3 rounded-sm bg-slate-950/75 px-1 h-4 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-200">
                {{ output.outputType }}
              </span>

              <div class="absolute inset-0 flex items-center justify-center">
                <i class="bi bi-broadcast-pin text-5xl text-white/15 transition-transform duration-500"></i>
              </div>

              <div class="absolute inset-x-3 bottom-3 flex-col text-[10px]">
                <p class="truncate text-xs font-semibold text-white">{{ output.name || output.displayName }}</p>
                <p class="mt-1 truncate text-[10px] text-slate-300/85">
                  {{ output.location ? parseLocation(output.location) : 'No location assigned' }}
                </p>
              </div>
            </div>

            <div class="space-y-3 p-4">
              <div class="flex flex-wrap gap-2">
                <span
                  v-if="getOutputProtocol(output)"
                  class="rounded-full border border-white/10 bg-slate-800/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-300"
                >
                  {{ getOutputProtocol(output) }}
                </span>
                <span
                  class="rounded-full border border-white/10 bg-slate-800/40 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400"
                >
                  {{ getOutputState(output.id) || 'UNKNOWN' }}
                </span>
              </div>

              <div class="space-y-1">
                <p class="truncate text-sm text-slate-200">{{ matrixOutputSummary(output) }}</p>
                <p class="truncate text-xs uppercase tracking-[0.16em] text-slate-500">{{ matrixOutputMeta(output) }}</p>
              </div>

              <div class="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                <span>{{ outputStatusTooltip(output) }}</span>
                <span class="text-blue-300 transition-colors group-hover:text-blue-200">Open details</span>
              </div>
            </div>
          </button>
        </div>

        <div v-else class="grid gap-2">
          <div
            v-for="output in filteredOutputs"
            :key="output.id"
            class="flex items-center gap-4 px-4 py-3 rounded bg-slate-800/50 border-none border-slate-700/40 hover:border-slate-600/60 transition-colors"
          >
            <!-- Status Indicator -->
            <div
              class="size-2.5 rounded-full shrink-0"
              :class="outputStatusDotClass(output)"
              :title="outputStatusTooltip(output)"
            ></div>

            <!-- Output Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-sm text-slate-200 truncate">{{ output.name || output.displayName }}</span>
                <span class="text-[10px] px-1.5 py-0.5 h-4 rounded uppercase tracking-wide shrink-0 bg-orange-900/40 text-orange-400">
                  {{ output.outputType }}
                </span>
                <span v-if="output.location" class="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-500 shrink-0">
                  {{ parseLocation(output.location) }}
                </span>
              </div>

              <!-- Output details (MEDIA_CONNECT) -->
              <div v-if="getOutputInfo(output.id)" class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
                <!-- Protocol -->
                <span v-if="getOutputProtocol(output)" class="px-1.5 py-0.5 rounded bg-slate-800/60 text-slate-400 border border-slate-700/30">
                  {{ getOutputProtocol(output) }}
                </span>

                <!-- Routed State -->
                <template v-if="getOutputRoutedState(output)">
                  <span class="text-slate-700 select-none">|</span>
                  <span
                    class="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide font-bold"
                    :class="getOutputRoutedState(output) === 'ROUTED'
                      ? 'bg-green-900/20 text-green-400 border border-green-800/20'
                      : 'bg-slate-800/60 text-slate-500 border border-slate-700/30'"
                  >
                    {{ getOutputRoutedState(output) }}
                  </span>
                </template>

                <!-- Destination IP -->
                <span class="text-slate-700 select-none">|</span>
                <span v-if="getOutputDestinationIp(output)" class="flex items-center gap-1.5 px-1.5 py-0.5 rounded bg-green-900/20 text-green-400 border border-green-800/20">
                  <span class="text-[9px] font-bold opacity-50">DST</span>
                  <span class="font-mono">{{ getOutputDestinationIp(output) }}</span>
                </span>
                <span v-else class="flex items-center gap-1.5 px-1.5 py-0.5 rounded bg-red-900/20 text-red-400 border border-red-800/20">
                  <span class="text-[9px] font-bold opacity-50">DST</span>
                  <span class="italic text-[10px]">Not connected</span>
                </span>

                <!-- Routed Input -->
                <template v-if="output.routedInput">
                  <span class="text-slate-700 select-none">|</span>
                  <span class="flex items-center gap-1.5 px-2 py-0.5 rounded bg-slate-800/40 text-slate-400 border border-slate-700/30">
                    <i class="bi bi-box-arrow-in-right text-[10px]"></i>
                    <span>{{ output.routedInput.name }} <span class="text-[9px] opacity-50">INPUT</span></span>
                  </span>
                </template>

                <a
                  v-if="getOutputInfo(output.id).Name"
                  :href="getGrafanaOutputUrl(getOutputInfo(output.id).Name)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-400 hover:text-blue-200 transition-colors flex items-center gap-1 bg-blue-400/10 px-2 py-0.5 rounded border border-blue-400/20"
                  @click.stop
                >
                  <i class="bi bi-graph-up text-[10px]"></i>
                  Metrics ↗
                </a>
              </div>

              <!-- Messages (errors/warnings) -->
              <div v-if="getOutputMessages(output).length > 0" class="mt-1.5 flex flex-col gap-1">
                <div
                  v-for="(msg, idx) in getOutputMessages(output)"
                  :key="idx"
                  class="text-xs flex items-start gap-1.5"
                  :class="messageClass(msg)"
                >
                  <span class="shrink-0 mt-0.5">{{ messageIcon(msg) }}</span>
                  <span>{{ msg.Message }}</span>
                </div>
              </div>
            </div>

            <!-- State + Health -->
            <div class="shrink-0 text-right flex flex-col items-end gap-1">
              <span
                v-if="getOutputState(output.id)"
                class="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide"
                :class="getOutputState(output.id) === 'ACTIVE'
                  ? 'bg-green-900/30 text-green-400'
                  : 'bg-slate-700/50 text-slate-400'"
              >
                {{ getOutputState(output.id) }}
              </span>
              <span
                class="text-xs px-2 py-1 rounded"
                :class="outputStatusLabelClass(output)"
              >
                {{ outputStatusLabel(output) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <transition name="source-monitoring-panel">
      <div
        v-if="selectedMatrixItem && selectedMatrixEntity"
        class="fixed inset-0 z-50 top-11"
      >
        <button
          class="absolute inset-0 bg-slate-950/80 backdrop-blur-[1px]"
          aria-label="Close details"
          @click="closeMatrixDetails"
        >
      
        </button>

        <div class="absolute right-0 top-0 h-full w-full max-w-sm overflow-y-auto bg-[#13191d] shadow-2xl">
          <div class="border-b border-white/10 px-5 py-4">
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0">
                <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {{ selectedMatrixItem.tab === 'inputs' ? 'Input details' : 'Output details' }}
                </p>
                <h3 class="mt-1 truncate text-lg font-semibold text-slate-100">
                  {{ selectedMatrixItem.tab === 'inputs' ? selectedMatrixEntity.name : (selectedMatrixEntity.name || selectedMatrixEntity.displayName) }}
                </h3>
                <div class="mt-2 flex flex-wrap gap-2">
                  <span
                    class="rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
                    :class="selectedMatrixItem.tab === 'inputs'
                      ? inputTypeBadgeClass(selectedMatrixEntity.type)
                      : 'bg-orange-900/40 text-orange-400'"
                  >
                    {{ selectedMatrixItem.tab === 'inputs' ? selectedMatrixEntity.type : selectedMatrixEntity.outputType }}
                  </span>
                  <span class="rounded-full bg-slate-800/60 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-300">
                    {{ selectedMatrixItem.tab === 'inputs' ? (getInputState(selectedMatrixEntity.id) || 'UNKNOWN') : (getOutputState(selectedMatrixEntity.id) || 'UNKNOWN') }}
                  </span>
                  <span
                    class="rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
                    :class="selectedMatrixItem.tab === 'inputs'
                      ? statusLabelClass(selectedMatrixEntity)
                      : outputStatusLabelClass(selectedMatrixEntity)"
                  >
                    {{ selectedMatrixItem.tab === 'inputs' ? statusLabel(selectedMatrixEntity) : outputStatusLabel(selectedMatrixEntity) }}
                  </span>
                </div>
              </div>

              <button
                class="h-9 w-9 shrink-0 flex items-center justify-center rounded border border-white/10 text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
                aria-label="Close details"
                @click="closeMatrixDetails"
              >
                 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48" id="Delete-1--Streamline-Plump" height="48" width="48" class="w-3 h-3">
                  <g id="Solid/Interface Essential/Add Remove Delete/delete-1--remove-add-button-buttons-delete-cross-x-mathematics-multiply-math">
                    <path id="Union" fill="currentColor" d="M44.7021 0.7704c-0.6868 -0.3728 -1.3995 -0.3517 -2.035 -0.1323 -0.6219 0.2148 -1.1856 0.6222 -1.6463 1.0605 -2.5083 2.387 -8.5323 8.1658 -17.0209 16.6073C15.5115 9.8644 9.4874 4.0856 6.9791 1.6986 6.5186 1.2603 5.9548 0.8529 5.3329 0.6381 4.6974 0.4187 3.9847 0.3976 3.2979 0.7704c-0.4425 0.2401 -0.9254 0.5908 -1.431 1.0964 -0.5055 0.5055 -0.8562 0.9885 -1.0964 1.4309 -0.3728 0.6868 -0.3517 1.3996 -0.1322 2.0351 0.2147 0.6219 0.6222 1.1856 1.0605 1.6462 2.3869 2.5083 8.1657 8.5324 16.6073 17.0209C9.8644 32.4886 4.0854 38.5127 1.6984 41.0211c-0.4382 0.4605 -0.8457 1.0242 -1.0604 1.6461 -0.2195 0.6356 -0.2406 1.3483 0.1322 2.0351 0.2402 0.4424 0.5909 0.9254 1.0964 1.431 0.5056 0.5054 0.9885 0.8561 1.431 1.0964 0.6868 0.3727 1.3995 0.3516 2.035 0.1322 0.6219 -0.2148 1.1856 -0.6222 1.6462 -1.0605C9.4871 43.9144 15.5113 38.1356 24 29.6938c8.4887 8.4418 14.5129 14.2206 17.0212 16.6076 0.4605 0.4383 1.0242 0.8457 1.6461 1.0605 0.6356 0.2194 1.3483 0.2405 2.0351 -0.1322 0.4424 -0.2403 0.9254 -0.591 1.431 -1.0964 0.5055 -0.5056 0.8562 -0.9886 1.0964 -1.431 0.3728 -0.6868 0.3517 -1.3995 0.1322 -2.0351 -0.2147 -0.6219 -0.6222 -1.1856 -1.0605 -1.6461 -2.387 -2.5084 -8.1658 -8.5325 -16.6076 -17.0212C38.1354 15.5114 43.9143 9.4873 46.3012 6.979c0.4384 -0.4606 0.8457 -1.0243 1.0605 -1.6462 0.2195 -0.6355 0.2406 -1.3483 -0.1323 -2.0351 -0.2401 -0.4424 -0.5908 -0.9254 -1.0964 -1.4309 -0.5055 -0.5055 -0.9884 -0.8562 -1.4309 -1.0964Z" stroke-width="1"></path>
                  </g>
                </svg>
              </button>
            </div>
          </div>

          <div class="space-y-5 p-5">
            <div class="overflow-hidden rounded-md border border-white/10 bg-slate-900/80">
              <div
                v-if="selectedMatrixItem.tab === 'inputs'"
                class="relative aspect-video bg-slate-900/80"
              >
                <img
                  v-if="getThumbnailData(selectedMatrixEntity).data && !getThumbnailData(selectedMatrixEntity).error"
                  :src="getThumbnailData(selectedMatrixEntity).data"
                  :alt="`${selectedMatrixEntity.name} thumbnail`"
                  class="h-full w-full object-cover"
                />
                <div
                  v-else-if="getThumbnailData(selectedMatrixEntity).loading"
                  class="flex h-full w-full items-center justify-center text-slate-400"
                >
                  <i class="bi bly-loading animate-spin text-lg"></i>
                </div>
                <div
                  v-else
                  class="flex h-full w-full items-center justify-center text-slate-500"
                >
                  <i class="bi bly-image text-3xl"></i>
                </div>
              </div>
              <div
                v-else
                class="relative flex aspect-video items-center justify-center bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.22),_transparent_36%),linear-gradient(135deg,_rgba(15,23,42,0.95),_rgba(17,24,39,0.95))]"
              >
                <i class="bi bi-broadcast-pin text-6xl text-white/15"></i>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div class="rounded-md border border-white/10 bg-[#12191e] p-4">
                <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Connection</p>
                <p class="mt-2 text-sm text-slate-100">
                  {{ selectedMatrixItem.tab === 'inputs' ? matrixInputSummary(selectedMatrixEntity) : matrixOutputSummary(selectedMatrixEntity) }}
                </p>
                <p class="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500">
                  {{ selectedMatrixItem.tab === 'inputs' ? matrixInputMeta(selectedMatrixEntity) : matrixOutputMeta(selectedMatrixEntity) }}
                </p>
              </div>

              <div class="rounded-md border border-white/10 bg-[#12191e] p-4">
                <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Location</p>
                <p class="mt-2 text-sm text-slate-100">
                  {{ selectedMatrixEntity.location ? parseLocation(selectedMatrixEntity.location) : 'No location assigned' }}
                </p>
                <p class="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500">
                  {{ selectedMatrixItem.tab === 'inputs' ? (isInputLive(selectedMatrixEntity.id) ? 'Live event linked' : 'Not linked to live event') : (isOutputLive(selectedMatrixEntity.id) ? 'Live event linked' : 'Not linked to live event') }}
                </p>
              </div>
            </div>

            <div class="rounded-md border border-white/10 bg-[#12191e] p-4">
              <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Connection details</p>

              <div
                v-if="selectedMatrixItem.tab === 'inputs'"
                class="mt-3 flex flex-wrap gap-2 text-xs"
              >
                <span
                  v-if="getProtocol(selectedMatrixEntity)"
                  class="rounded-full border border-white/10 bg-slate-800/60 px-2 py-1 text-slate-300"
                >
                  {{ getProtocol(selectedMatrixEntity) }}
                </span>
                <span
                  class="rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]"
                  :class="connectionStatusClass(selectedMatrixEntity)"
                >
                  {{ connectionStatusLabel(selectedMatrixEntity) }}
                </span>
                <span
                  v-for="source in getMediaConnectSources(selectedMatrixEntity)"
                  :key="`details-${selectedMatrixEntity.id}-${source.label}`"
                  class="rounded-full border px-2 py-1"
                  :class="source.connected
                    ? 'border-green-800/20 bg-green-900/20 text-green-300'
                    : 'border-red-800/20 bg-red-900/20 text-red-300'"
                >
                  {{ source.label }}: {{ source.connected ? source.ip : 'Not Connected' }}
                </span>
                <span
                  v-if="selectedMatrixInfo?.destination?.url"
                  class="rounded-full border border-indigo-800/20 bg-indigo-900/20 px-2 py-1 text-indigo-300"
                >
                  Destination: {{ selectedMatrixInfo.destination.url }}
                </span>
                <span
                  v-if="selectedMatrixInfo?.connectedClients != null"
                  class="rounded-full border border-white/10 bg-slate-800/40 px-2 py-1 text-slate-300"
                >
                  Clients: {{ selectedMatrixInfo.connectedClients }}
                </span>
                <span
                  v-if="selectedMatrixInfo?.RoutedOutputs != null"
                  class="rounded-full border border-white/10 bg-slate-800/40 px-2 py-1 text-slate-300"
                >
                  Outputs: {{ selectedMatrixInfo.RoutedOutputs }}
                </span>
              </div>

              <div
                v-else
                class="mt-3 flex flex-wrap gap-2 text-xs"
              >
                <span
                  v-if="getOutputProtocol(selectedMatrixEntity)"
                  class="rounded-full border border-white/10 bg-slate-800/60 px-2 py-1 text-slate-300"
                >
                  {{ getOutputProtocol(selectedMatrixEntity) }}
                </span>
                <span
                  v-if="getOutputDestinationIp(selectedMatrixEntity)"
                  class="rounded-full border border-green-800/20 bg-green-900/20 px-2 py-1 text-green-300"
                >
                  Destination IP: {{ getOutputDestinationIp(selectedMatrixEntity) }}
                </span>
                <span
                  v-if="selectedMatrixEntity.routedInput?.name"
                  class="rounded-full border border-white/10 bg-slate-800/40 px-2 py-1 text-slate-300"
                >
                  Routed input: {{ selectedMatrixEntity.routedInput.name }}
                </span>
                <span
                  v-if="getOutputRoutedState(selectedMatrixEntity)"
                  class="rounded-full border border-white/10 bg-slate-800/40 px-2 py-1 text-slate-300"
                >
                  Routed state: {{ getOutputRoutedState(selectedMatrixEntity) }}
                </span>
              </div>
            </div>

            <div class="flex flex-wrap gap-2">
              <a
                v-if="selectedMatrixItem.tab === 'inputs' && selectedMatrixInfo?.Name"
                :href="getGrafanaUrl(selectedMatrixInfo.Name)"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex h-10 items-center gap-2 rounded-full bg-blue-500/15 px-4 text-sm font-semibold text-blue-300 transition-colors hover:bg-blue-500/25 hover:text-blue-200"
              >
                <i class="bi bi-graph-up"></i>
                Metrics
              </a>
              <a
                v-if="selectedMatrixItem.tab === 'outputs' && selectedMatrixInfo?.Name"
                :href="getGrafanaOutputUrl(selectedMatrixInfo.Name)"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex h-10 items-center gap-2 rounded-full bg-blue-500/15 px-4 text-sm font-semibold text-blue-300 transition-colors hover:bg-blue-500/25 hover:text-blue-200"
              >
                <i class="bi bi-graph-up"></i>
                Metrics
              </a>
            </div>

            <div
              v-if="selectedMatrixItem.tab === 'inputs' ? getInputMessages(selectedMatrixEntity).length > 0 : getOutputMessages(selectedMatrixEntity).length > 0"
              class="rounded-md border border-white/10 bg-[#12191e] p-4"
            >
              <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Messages</p>
              <div class="mt-3 flex flex-col gap-2">
                <div
                  v-for="(msg, idx) in selectedMatrixItem.tab === 'inputs' ? getInputMessages(selectedMatrixEntity) : getOutputMessages(selectedMatrixEntity)"
                  :key="idx"
                  class="flex items-start gap-2 text-sm"
                  :class="messageClass(msg)"
                >
                  <span class="mt-0.5 shrink-0">{{ messageIcon(msg) }}</span>
                  <span>{{ msg.Message }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

const SOURCE_MONITORING_FILTERS_KEY = 'source-monitoring-filters';
const DEFAULT_TAB_FILTER_VALUES = {
  searchQuery: '',
  selectedType: '',
  sortBy: 'name-asc',
  healthFilter: 'all',
  liveOnly: true,
  viewMode: 'list',
};

function createDefaultTabFilters() {
  return {
    inputs: { ...DEFAULT_TAB_FILTER_VALUES },
    outputs: { ...DEFAULT_TAB_FILTER_VALUES },
  };
}

function debounce(fn, delay) {
  let timer = null;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

export default {
  name: 'SourceMonitoring',

  data() {
    return {
      activeTab: 'inputs',
      tabFilters: createDefaultTabFilters(),
      healthFilters: [
        { label: 'All', value: 'all' },
        { label: 'Healthy', value: 'healthy' },
        { label: 'Errored', value: 'errored' },
        { label: 'Unknown', value: 'unknown' },
      ],
      pollingTimer: null,
      liveAssignmentsPollingTimer: null,
      thumbnailPollingTimer: null,
      inputThumbnails: {},
      isRestoringFilters: false,
      isFiltersDrawerOpen: false,
      selectedMatrixItem: null,
    };
  },

  computed: {
    ...mapState({
      inputs: (state) => state.sourceMonitoring.inputs,
      inputInfos: (state) => state.sourceMonitoring.inputInfos,
      outputs: (state) => state.sourceMonitoring.outputs,
      outputInfos: (state) => state.sourceMonitoring.outputInfos,
      liveInputIds: (state) => state.sourceMonitoring.liveInputIds,
      liveOutputIds: (state) => state.sourceMonitoring.liveOutputIds,
      loading: (state) => state.sourceMonitoring.loading,
      loadingStatuses: (state) => state.sourceMonitoring.loadingStatuses,
      loadingLiveAssignments: (state) => state.sourceMonitoring.loadingLiveAssignments,
    }),
    ...mapGetters('sourceMonitoring', [
      'availableTypes', 'isInputHealthy', 'getInputInfo', 'getInputState',
      'availableOutputTypes', 'isOutputHealthy', 'getOutputInfo', 'getOutputState',
    ]),

    currentAvailableTypes() {
      return this.activeTab === 'inputs' ? this.availableTypes : this.availableOutputTypes;
    },

    currentTabFilters() {
      return this.tabFilters[this.activeTab] || this.tabFilters.inputs;
    },

    sortButtonLabel() {
      return this.sortBy === 'name-desc' ? 'Name Z-A' : 'Name A-Z';
    },

    sortNameDesc() {
      return this.sortBy === 'name-desc';
    },

    overlayActive() {
      return this.isFiltersDrawerOpen || Boolean(this.selectedMatrixItem);
    },

    mobileFiltersCount() {
      let count = 0;
      if (this.searchQuery) count += 1;
      if (this.selectedType) count += 1;
      if (this.healthFilter !== DEFAULT_TAB_FILTER_VALUES.healthFilter) count += 1;
      if (this.sortBy !== DEFAULT_TAB_FILTER_VALUES.sortBy) count += 1;
      if (this.liveOnly !== DEFAULT_TAB_FILTER_VALUES.liveOnly) count += 1;
      return count;
    },

    mobileFiltersActive() {
      return this.mobileFiltersCount > 0;
    },

    searchQuery: {
      get() {
        return this.currentTabFilters.searchQuery;
      },
      set(value) {
        this.tabFilters[this.activeTab].searchQuery = value;
      },
    },

    selectedType: {
      get() {
        return this.currentTabFilters.selectedType;
      },
      set(value) {
        this.tabFilters[this.activeTab].selectedType = value;
      },
    },

    healthFilter: {
      get() {
        return this.currentTabFilters.healthFilter;
      },
      set(value) {
        this.tabFilters[this.activeTab].healthFilter = value;
      },
    },

    sortBy: {
      get() {
        return this.currentTabFilters.sortBy;
      },
      set(value) {
        this.tabFilters[this.activeTab].sortBy = value;
      },
    },

    liveOnly: {
      get() {
        return this.currentTabFilters.liveOnly;
      },
      set(value) {
        this.tabFilters[this.activeTab].liveOnly = value;
      },
    },

    viewMode: {
      get() {
        return this.currentTabFilters.viewMode;
      },
      set(value) {
        this.tabFilters[this.activeTab].viewMode = value;
      },
    },

    liveInputIdsSet() {
      return new Set(this.liveInputIds);
    },

    liveOutputIdsSet() {
      return new Set(this.liveOutputIds);
    },

    selectedMatrixEntity() {
      if (!this.selectedMatrixItem) return null;
      const collection = this.selectedMatrixItem.tab === 'inputs' ? this.inputs : this.outputs;
      return collection.find((item) => item.id === this.selectedMatrixItem.itemId) || null;
    },

    selectedMatrixInfo() {
      if (!this.selectedMatrixItem || !this.selectedMatrixEntity) return null;
      return this.selectedMatrixItem.tab === 'inputs'
        ? this.getInputInfo(this.selectedMatrixEntity.id)
        : this.getOutputInfo(this.selectedMatrixEntity.id);
    },

    persistedFilters() {
      return {
        activeTab: this.activeTab,
        tabFilters: this.tabFilters,
      };
    },

    filteredInputIds() {
      return this.filteredInputs.map((input) => input.id);
    },

    filteredInputs() {
      let list = this.inputs;

      if (this.selectedType) {
        list = list.filter((input) => input.type === this.selectedType);
      }

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(
          (input) =>
            (input.name && input.name.toLowerCase().includes(q)) ||
            (input.location && this.parseLocation(input.location).toLowerCase().includes(q)),
        );
      }

      if (this.healthFilter !== 'all') {
        list = list.filter((input) => {
          const healthy = this.isInputHealthy(input.id);
          if (this.healthFilter === 'unknown') return healthy === null;
          if (healthy === null) return false;
          return this.healthFilter === 'healthy' ? healthy : !healthy;
        });
      }

      if (this.liveOnly) {
        list = list.filter((input) => this.liveInputIdsSet.has(input.id));
      }

      list = this.sortListByName(list, (input) => input.name);

      return list;
    },

    filteredOutputIds() {
      return this.filteredOutputs.map((output) => output.id);
    },

    filteredOutputs() {
      let list = this.outputs.filter((output) => output.outputType === 'MEDIA_CONNECT');

      if (this.selectedType) {
        list = list.filter((output) => output.outputType === this.selectedType);
      }

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(
          (output) =>
            (output.name && output.name.toLowerCase().includes(q)) ||
            (output.displayName && output.displayName.toLowerCase().includes(q)) ||
            (output.location && this.parseLocation(output.location).toLowerCase().includes(q)),
        );
      }

      if (this.healthFilter !== 'all') {
        list = list.filter((output) => {
          const healthy = this.isOutputHealthy(output.id);
          if (this.healthFilter === 'unknown') return healthy === null;
          if (healthy === null) return false;
          return this.healthFilter === 'healthy' ? healthy : !healthy;
        });
      }

      if (this.liveOnly) {
        list = list.filter((output) => this.liveOutputIdsSet.has(output.id));
      }

      list = this.sortListByName(list, (output) => output.name || output.displayName);

      return list;
    },
  },

  watch: {
    filteredInputIds: {
      handler: 'handleFilteredInputIdsChange',
    },
    filteredOutputIds: {
      handler: 'debouncedFetchFilteredOutputInfos',
    },
    activeTab() {
      if (this.isRestoringFilters) {
        return;
      }
      this.closeFiltersDrawer();
      this.closeMatrixDetails();
      this.restartPolling();
      this.fetchVisibleInputThumbnails();
      this.persistFilters();
    },
    overlayActive: 'syncBodyScroll',
    searchQuery: 'persistFilters',
    selectedType: 'persistFilters',
    healthFilter: 'persistFilters',
    sortBy: 'persistFilters',
    liveOnly: 'persistFilters',
    viewMode: 'persistFilters',
  },

  created() {
    this.debouncedFetchFilteredInfos = debounce(function () {
      const ids = this.filteredInputIds;
      if (ids.length > 0) {
        this.fetchInputInfos(ids);
      }
    }, 500);
    this.debouncedFetchFilteredOutputInfos = debounce(function () {
      const ids = this.filteredOutputIds;
      if (ids.length > 0) {
        this.fetchOutputInfos(ids);
      }
    }, 500);
    this.debouncedFetchFilteredThumbnails = debounce(function () {
      this.fetchVisibleInputThumbnails();
    }, 500);
    this.restoreFilters();
  },

  async mounted() {
    await Promise.all([this.fetchInputs(), this.fetchOutputs(), this.fetchLiveAssignments()]);
    if (this.activeTab === 'inputs') {
      await Promise.all([
        this.fetchInputInfos(this.filteredInputIds),
        this.fetchVisibleInputThumbnails(),
      ]);
    } else {
      await this.fetchOutputInfos(this.filteredOutputIds);
    }
    this.startPolling();
    this.startLiveAssignmentsPolling();
    this.startThumbnailPolling();
  },

  beforeDestroy() {
    this.stopPolling();
    this.stopLiveAssignmentsPolling();
    this.stopThumbnailPolling();
    this.closeFiltersDrawer();
    this.closeMatrixDetails();
    this.syncBodyScroll();
  },

  methods: {
    ...mapActions('sourceMonitoring', ['fetchInputs', 'fetchInputInfos', 'fetchOutputs', 'fetchOutputInfos', 'fetchLiveAssignments']),
    ...mapActions('routerAlt', ['fetchRouterThumbnails']),

    async refresh() {
      await this.fetchLiveAssignments();
      if (this.activeTab === 'inputs') {
        await this.fetchInputs();
        await Promise.all([
          this.fetchInputInfos(this.filteredInputIds),
          this.fetchVisibleInputThumbnails(),
        ]);
      } else {
        await this.fetchOutputs();
        await this.fetchOutputInfos(this.filteredOutputIds);
      }
    },

    startPolling() {
      this.stopPolling();
      this.pollingTimer = setInterval(() => {
        if (this.activeTab === 'inputs') {
          this.fetchInputInfos(this.filteredInputIds);
        } else {
          this.fetchOutputInfos(this.filteredOutputIds);
        }
      }, 5000);
    },

    startLiveAssignmentsPolling() {
      this.stopLiveAssignmentsPolling();
      this.liveAssignmentsPollingTimer = setInterval(() => {
        this.fetchLiveAssignments();
      }, 30000);
    },

    startThumbnailPolling() {
      this.stopThumbnailPolling();
      this.thumbnailPollingTimer = setInterval(() => {
        this.fetchVisibleInputThumbnails();
      }, 60000);
    },

    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },

    stopLiveAssignmentsPolling() {
      if (this.liveAssignmentsPollingTimer) {
        clearInterval(this.liveAssignmentsPollingTimer);
        this.liveAssignmentsPollingTimer = null;
      }
    },

    stopThumbnailPolling() {
      if (this.thumbnailPollingTimer) {
        clearInterval(this.thumbnailPollingTimer);
        this.thumbnailPollingTimer = null;
      }
    },

    restartPolling() {
      this.stopPolling();
      this.startPolling();
    },

    syncBodyScroll() {
      if (typeof document === 'undefined') {
        return;
      }
      document.body.style.overflow = this.overlayActive ? 'hidden' : '';
    },

    openFiltersDrawer() {
      this.isFiltersDrawerOpen = true;
    },

    closeFiltersDrawer() {
      this.isFiltersDrawerOpen = false;
    },

    toggleSortBy() {
      this.sortBy = this.sortBy === 'name-desc' ? 'name-asc' : 'name-desc';
    },

    openMatrixDetails(item) {
      this.selectedMatrixItem = {
        tab: this.activeTab,
        itemId: item.id,
      };
    },

    closeMatrixDetails() {
      this.selectedMatrixItem = null;
    },

    resetCurrentTabFilters() {
      this.tabFilters[this.activeTab] = {
        ...DEFAULT_TAB_FILTER_VALUES,
      };
      this.persistFilters();
    },

    handleFilteredInputIdsChange() {
      this.debouncedFetchFilteredInfos();
      this.debouncedFetchFilteredThumbnails();
    },

    persistFilters() {
      localStorage.setItem(
        SOURCE_MONITORING_FILTERS_KEY,
        JSON.stringify(this.persistedFilters),
      );
    },

    restoreFilters() {
      const savedFilters = localStorage.getItem(SOURCE_MONITORING_FILTERS_KEY);
      if (!savedFilters) return;

      try {
        const parsedFilters = JSON.parse(savedFilters);
        this.isRestoringFilters = true;
        const defaultTabFilters = createDefaultTabFilters();

        if (parsedFilters.tabFilters) {
          this.tabFilters = {
            inputs: {
              ...defaultTabFilters.inputs,
              ...(parsedFilters.tabFilters.inputs || {}),
            },
            outputs: {
              ...defaultTabFilters.outputs,
              ...(parsedFilters.tabFilters.outputs || {}),
            },
          };
        } else {
          const legacyFilters = {
            searchQuery: parsedFilters.searchQuery || '',
            selectedType: parsedFilters.selectedType || '',
            sortBy: parsedFilters.sortBy || 'name-asc',
            healthFilter: parsedFilters.healthFilter || 'all',
            liveOnly: typeof parsedFilters.liveOnly === 'boolean'
              ? parsedFilters.liveOnly
              : true,
            viewMode: parsedFilters.viewMode || 'list',
          };

          this.tabFilters = {
            inputs: {
              ...defaultTabFilters.inputs,
              ...legacyFilters,
            },
            outputs: {
              ...defaultTabFilters.outputs,
            },
          };
        }

        this.activeTab = parsedFilters.activeTab || 'inputs';
      } catch (error) {
        console.error('Failed to restore Source Monitoring filters', error);
      } finally {
        this.isRestoringFilters = false;
      }
    },

    async fetchVisibleInputThumbnails() {
      if (this.activeTab !== 'inputs' || this.filteredInputIds.length === 0) return;

      const visibleInputs = this.filteredInputs.reduce((acc, input) => {
        if (!acc.some((item) => item.id === input.id)) {
          acc.push(input);
        }
        return acc;
      }, []);
      const probeInputs = visibleInputs.filter((input) => input.probeProjectId && input.probeTaskId);
      const routerInputs = visibleInputs.filter((input) => !(input.probeProjectId && input.probeTaskId));
      const fetchableInputs = [...probeInputs, ...routerInputs];

      if (fetchableInputs.length === 0) return;

      const inputIds = fetchableInputs.map((input) => input.id);
      const timestamp = new Date();

      inputIds.forEach((inputId) => {
        const currentThumbnail = this.inputThumbnails[inputId];
        this.$set(this.inputThumbnails, inputId, {
          data: currentThumbnail?.data || null,
          timestamp: currentThumbnail?.timestamp || null,
          loading: true,
          error: null,
          warning: null,
        });
      });

      try {
        const fetchTasks = [];

        if (probeInputs.length > 0) {
          const probeTask = async () => {
            const projects = probeInputs.map((input) => ({
              projectId: input.probeProjectId,
              taskId: input.probeTaskId,
            }));
            const Servers = (await import('@/services/Servers')).default;
            const response = await Servers.getInputThumbs({ projects });
            const thumbsData = response?.data || {};

            probeInputs.forEach((input) => {
              const thumbnailData = thumbsData[input.probeProjectId];
              this.$set(this.inputThumbnails, input.id, {
                data: thumbnailData?.[input.probeTaskId]?.data || null,
                timestamp,
                loading: false,
                error: null,
                warning: null,
              });
            });
          };
          fetchTasks.push(probeTask());
        }

        if (routerInputs.length > 0) {
          const routerTask = async () => {
            const { thumbnails = {}, errors = [] } = await this.fetchRouterThumbnails({
              inputIds: routerInputs.map((input) => input.id),
              includeErrors: true,
            });
            const routerErrors = this.mapRouterThumbnailErrors(errors);

            routerInputs.forEach((input) => {
              const parsedThumbnailIssue = this.parseRouterThumbnailIssue(routerErrors[input.id]);
              this.$set(this.inputThumbnails, input.id, {
                data: thumbnails[input.id] || null,
                timestamp,
                loading: false,
                error: parsedThumbnailIssue && !parsedThumbnailIssue.warning
                  ? parsedThumbnailIssue.message
                  : null,
                warning: parsedThumbnailIssue?.warning ? parsedThumbnailIssue.message : null,
              });
            });
          };
          fetchTasks.push(routerTask());
        }

        await Promise.all(fetchTasks);
      } catch (error) {
        inputIds.forEach((inputId) => {
          this.$set(this.inputThumbnails, inputId, {
            data: null,
            timestamp,
            loading: false,
            error: 'Failed to fetch thumbnail',
            warning: null,
          });
        });
      }
    },

    mapRouterThumbnailErrors(errors) {
      if (!Array.isArray(errors)) return {};

      return errors.reduce((acc, entry) => {
        if (!entry?.inputId || !entry?.error) return acc;
        acc[entry.inputId] = entry.error;
        return acc;
      }, {});
    },

    parseRouterThumbnailIssue(errorMessage) {
      if (!errorMessage || typeof errorMessage !== 'string') return null;

      const cleanedMessage = errorMessage.trim();
      if (!cleanedMessage) return null;

      const isInputNotActive = /router input must be in the active state/i.test(cleanedMessage);
      if (isInputNotActive) {
        return {
          warning: true,
          message: cleanedMessage,
        };
      }

      return {
        warning: false,
        message: cleanedMessage,
      };
    },

    getThumbnailData(input) {
      const thumbnailData = this.inputThumbnails[input.id] || {
        data: null,
        loading: false,
        error: null,
        warning: null,
        timestamp: null,
      };
      console.log('Thumbnail data for input', input.id, thumbnailData);

      let imgData = thumbnailData.data;
      if (typeof imgData !== 'string') {
        imgData = null;
      } else {
        imgData = imgData.trim();
        if (!imgData) {
          imgData = null;
        } else if (!imgData.startsWith('data:image')) {
          const isBase64 = /^[A-Za-z0-9+/=]+$/.test(imgData);
          imgData = isBase64 ? `data:image/jpeg;base64,${imgData}` : null;
        }
      }

      return {
        ...thumbnailData,
        data: imgData,
      };
    },

    sortListByName(list, getName) {
      const direction = this.sortBy === 'name-desc' ? -1 : 1;

      return [...list].sort((left, right) => {
        const leftName = (getName(left) || '').toLowerCase();
        const rightName = (getName(right) || '').toLowerCase();
        return leftName.localeCompare(rightName) * direction;
      });
    },

    thumbnailTooltip(input) {
      const thumbnailData = this.getThumbnailData(input);
      if (thumbnailData.warning) return `${input.name} • ${thumbnailData.warning}`;
      if (thumbnailData.error) return `${input.name} • ${thumbnailData.error}`;
      if (!thumbnailData.timestamp) return input.name;
      return `${input.name} • updated ${thumbnailData.timestamp.toLocaleTimeString()}`;
    },

    isInputLive(inputId) {
      return this.liveInputIdsSet.has(inputId);
    },

    isOutputLive(outputId) {
      return this.liveOutputIdsSet.has(outputId);
    },

    matrixInputSummary(input) {
      if (input.type === 'MEDIA_CONNECT') {
        const connectedIps = this.getSourceIps(input);
        return connectedIps.length > 0 ? connectedIps.join(', ') : 'No active source';
      }

      const info = this.getInputInfo(input.id);
      return info?.destination?.url || info?.source?.url || 'Tap to inspect connection';
    },

    matrixInputMeta(input) {
      const info = this.getInputInfo(input.id);
      if (input.type === 'MEDIA_CONNECT' && info?.RoutedOutputs != null) {
        return `${info.RoutedOutputs} outputs`;
      }
      if (info?.connectedClients != null) {
        return `${info.connectedClients} clients`;
      }
      return this.getInputState(input.id) || 'Unknown state';
    },

    matrixOutputSummary(output) {
      return this.getOutputDestinationIp(output) || this.getOutputDestination(output) || 'No active destination';
    },

    matrixOutputMeta(output) {
      if (output.routedInput?.name) {
        return `Input: ${output.routedInput.name}`;
      }
      return this.getOutputRoutedState(output) || this.getOutputState(output.id) || 'Tap to inspect connection';
    },

    getInputMessages(input) {
      const info = this.getInputInfo(input.id);
      if (!info || !info.Messages) return [];
      return info.Messages;
    },

    getProtocol(input) {
      const info = this.getInputInfo(input.id);
      if (!info?.Configuration?.Standard?.Protocol) return null;
      return info.Configuration.Standard.Protocol.replace(/_/g, ' ');
    },

    getGrafanaUrl(inputName) {
      return `https://g-931d259b22.grafana-workspace.eu-west-1.amazonaws.com/d/ffgbmgfjc8dtwrx/media-connect-output-monitor?orgId=1&var-InputName=${encodeURIComponent(inputName)}`;
    },

    getMediaConnectSources(input) {
      const info = this.getInputInfo(input.id);
      const streamDetails = info?.StreamDetails || {};
      const sources = [];

      const failover = streamDetails?.Failover;
      if (failover && typeof failover === 'object') {
        const failoverEntries = Object.entries(failover)
          .filter(([, details]) => details && typeof details === 'object' && Object.prototype.hasOwnProperty.call(details, 'SourceIndex'))
          .sort(([, left], [, right]) => {
            const leftIndex = Number.isFinite(left?.SourceIndex) ? left.SourceIndex : 0;
            const rightIndex = Number.isFinite(right?.SourceIndex) ? right.SourceIndex : 0;
            return leftIndex - rightIndex;
          });

        failoverEntries.forEach(([, details], position) => {
          const sourceIndex = Number.isFinite(details?.SourceIndex) ? details.SourceIndex : position;
          const ip = details?.SourceIpAddress || null;
          sources.push({
            index: sourceIndex,
            label: `SRC ${sourceIndex + 1}`,
            ip,
            connected: !!ip,
          });
        });
      }

      if (sources.length === 0) {
        const standardIp = streamDetails?.Standard?.SourceIpAddress || null;
        sources.push({ index: 0, label: 'SRC 1', ip: standardIp, connected: !!standardIp });
      }

      return sources;
    },

    getSourceIps(input) {
      return this.getMediaConnectSources(input)
        .filter((source) => source.connected)
        .map((source) => source.ip);
    },

    isSourceConnected(input) {
      return this.getMediaConnectSources(input).some((source) => source.connected);
    },

    connectionStatusLabel(input) {
      return this.isSourceConnected(input) ? 'Connected' : 'Not Connected';
    },

    connectionStatusClass(input) {
      return this.isSourceConnected(input)
        ? 'bg-green-900/20 text-green-400 border border-green-800/20'
        : 'bg-red-900/20 text-red-400 border border-red-800/20';
    },

    getSourceIp(input) {
      return this.getSourceIps(input)[0] || null;
    },

    parseLocation(location) {
      if (!location) return '';
      try {
        const parsed = JSON.parse(location);
        if (Array.isArray(parsed)) return parsed.join(', ');
        return String(parsed);
      } catch {
        return location;
      }
    },

    statusDotClass(input) {
      const info = this.getInputInfo(input.id);
      if (!info) return 'bg-slate-500';
      const healthy = this.isInputHealthy(input.id);
      if (healthy === false) return 'bg-red-500';
      const state = this.getInputState(input.id);
      if (state === 'ACTIVE') return 'bg-green-500';
      return 'bg-slate-500';
    },

    statusTooltip(input) {
      const info = this.getInputInfo(input.id);
      if (!info) return 'No status data';
      const healthy = this.isInputHealthy(input.id);
      if (healthy === false) return 'Has issues';
      const state = this.getInputState(input.id);
      if (state === 'ACTIVE') return 'Active';
      return state || 'Unknown';
    },

    statusLabel(input) {
      const healthy = this.isInputHealthy(input.id);
      if (healthy === null) return 'Unknown';
      return healthy ? 'Healthy' : 'Error';
    },

    statusLabelClass(input) {
      const healthy = this.isInputHealthy(input.id);
      if (healthy === null) return 'bg-slate-700/50 text-slate-400';
      return healthy
        ? 'bg-green-900/30 text-green-400'
        : 'bg-red-900/30 text-red-400';
    },

    inputTypeBadgeClass(type) {
      switch (type) {
        case 'MEDIA_CONNECT': return 'bg-orange-900/40 text-orange-400';
        case 'ROUTE': return 'bg-blue-900/40 text-blue-400';
        case 'TECHEX': return 'bg-[rgb(126,0,128)]/40 text-[rgb(200,150,201)]';
        default: return 'bg-slate-700/60 text-slate-400';
      }
    },

    messageClass(msg) {
      const code = msg.Code || '';
      if (code === 'StreamError' || code === 'Error' || code === 'Critical') return 'text-red-400';
      if (code === 'Warning') return 'text-yellow-400';
      return 'text-slate-400';
    },

    messageIcon(msg) {
      const code = msg.Code || '';
      if (code === 'StreamError' || code === 'Error' || code === 'Critical') return '✕';
      if (code === 'Warning') return '⚠';
      return 'ℹ';
    },

    // Output helpers
    getOutputMessages(output) {
      const info = this.getOutputInfo(output.id);
      if (!info || !info.Messages) return [];
      return info.Messages;
    },

    getOutputProtocol(output) {
      const info = this.getOutputInfo(output.id);
      if (!info?.Configuration?.Standard?.Protocol) return null;
      return info.Configuration.Standard.Protocol.replace(/_/g, ' ');
    },

    getOutputDestination(output) {
      const info = this.getOutputInfo(output.id);
      return info?.Destination?.Standard?.Url || null;
    },

    getOutputRoutedState(output) {
      const info = this.getOutputInfo(output.id);
      return info?.RoutedState || null;
    },

    getOutputDestinationIp(output) {
      const info = this.getOutputInfo(output.id);
      return info?.StreamDetails?.Standard?.DestinationIpAddress || null;
    },

    getGrafanaOutputUrl(outputName) {
      return `https://g-931d259b22.grafana-workspace.eu-west-1.amazonaws.com/d/ffgbmgfjc8dtwrx/media-connect-output-monitor?orgId=1&var-OutputName=${encodeURIComponent(outputName)}`;
    },

    outputStatusDotClass(output) {
      const info = this.getOutputInfo(output.id);
      if (!info) return 'bg-slate-500';
      const healthy = this.isOutputHealthy(output.id);
      if (healthy === false) return 'bg-red-500';
      const state = this.getOutputState(output.id);
      if (state === 'ACTIVE') return 'bg-green-500';
      return 'bg-slate-500';
    },

    outputStatusTooltip(output) {
      const info = this.getOutputInfo(output.id);
      if (!info) return 'No status data';
      const healthy = this.isOutputHealthy(output.id);
      if (healthy === false) return 'Has issues';
      const state = this.getOutputState(output.id);
      if (state === 'ACTIVE') return 'Active';
      return state || 'Unknown';
    },

    outputStatusLabel(output) {
      const healthy = this.isOutputHealthy(output.id);
      if (healthy === null) return 'Unknown';
      return healthy ? 'Healthy' : 'Error';
    },

    outputStatusLabelClass(output) {
      const healthy = this.isOutputHealthy(output.id);
      if (healthy === null) return 'bg-slate-700/50 text-slate-400';
      return healthy
        ? 'bg-green-900/30 text-green-400'
        : 'bg-red-900/30 text-red-400';
    },
  },
};
</script>

<style scoped>
.source-monitoring-drawer-enter-active,
.source-monitoring-drawer-leave-active {
  transition: opacity 0.2s ease;
}

.source-monitoring-drawer-enter-active .absolute.right-0,
.source-monitoring-drawer-leave-active .absolute.right-0 {
  transition: transform 0.24s ease;
}

.source-monitoring-drawer-enter,
.source-monitoring-drawer-leave-to {
  opacity: 0;
}

.source-monitoring-drawer-enter .absolute.right-0,
.source-monitoring-drawer-leave-to .absolute.right-0 {
  transform: translateX(100%);
}

.source-monitoring-panel-enter-active,
.source-monitoring-panel-leave-active {
  transition: opacity 0.2s ease;
}

.source-monitoring-panel-enter-active .absolute.right-0,
.source-monitoring-panel-leave-active .absolute.right-0 {
  transition: transform 0.24s ease;
}

.source-monitoring-panel-enter,
.source-monitoring-panel-leave-to {
  opacity: 0;
}

.source-monitoring-panel-enter .absolute.right-0,
.source-monitoring-panel-leave-to .absolute.right-0 {
  transform: translateX(100%);
}
</style>
